import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background text-foreground font-body overflow-auto fixed inset-0">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <header className="mb-8">
          <Link
            to="/"
            className="text-2xl sm:text-3xl font-display font-bold text-primary neon-text hover:opacity-80 transition-opacity"
          >
            SPACASNAKE<span className="text-secondary neon-text-magenta">.IO</span>
          </Link>
        </header>

        <h1 className="text-2xl sm:text-3xl font-display font-bold text-primary mb-6">
          Privacy Policy
        </h1>
        <p className="text-muted-foreground text-sm mb-8">
          Last updated: February 8, 2025
        </p>

        <div className="space-y-6 text-sm sm:text-base text-muted-foreground leading-relaxed">
          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              1. Introduction
            </h2>
            <p>
              Welcome to SpacaSnake.io ("we", "our", or "us"). We respect your
              privacy and are committed to protecting your personal data. This
              Privacy Policy explains how we collect, use, and safeguard your
              information when you use our website and game at spacasnake.io (the
              "Service").
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              2. Information We Collect
            </h2>
            <p className="mb-2">
              We do not require you to create an account or provide personal
              information to play the game. However, we may collect the following
              data automatically:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>
                <strong className="text-foreground">Usage data:</strong> Pages
                visited, time spent on the site, game sessions, and interactions
                via Google Analytics.
              </li>
              <li>
                <strong className="text-foreground">Device data:</strong>{' '}
                Browser type, operating system, screen resolution, and device
                type.
              </li>
              <li>
                <strong className="text-foreground">Cookies and similar technologies:</strong>{' '}
                Used by third-party services (see Section 4).
              </li>
              <li>
                <strong className="text-foreground">Local storage:</strong>{' '}
                Game preferences (e.g., sound settings, language, high scores)
                stored locally on your device using keys prefixed with{' '}
                <code className="text-primary">spacasnake_</code>. This data
                never leaves your device.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              3. How We Use Your Information
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>To operate and improve the game experience.</li>
              <li>To analyze usage trends and optimize performance.</li>
              <li>To display advertisements via Google AdSense.</li>
              <li>To remember your preferences (language, sound, quality).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              4. Third-Party Services
            </h2>
            <p className="mb-2">We use the following third-party services:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>
                <strong className="text-foreground">Google Analytics (GA4):</strong>{' '}
                Collects anonymized usage data to help us understand how
                players interact with the game. Google may use cookies to
                collect this information. Learn more at{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Google's Privacy Policy
                </a>
                .
              </li>
              <li>
                <strong className="text-foreground">Google AdSense:</strong>{' '}
                Displays advertisements on our site. Google and its partners may
                use cookies to serve ads based on your prior visits to this or
                other websites. You can opt out of personalized advertising by
                visiting{' '}
                <a
                  href="https://www.google.com/settings/ads"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Google Ads Settings
                </a>
                .
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              5. Cookies and Consent
            </h2>
            <p className="mb-2">
              We implement Google Consent Mode to respect your cookie
              preferences. By default, non-essential cookies (advertising and
              analytics) are disabled until you provide consent.
            </p>
            <p>
              You can manage your cookie preferences at any time through your
              browser settings. Most browsers allow you to block or delete
              cookies. Note that disabling cookies may affect your experience on
              the site.
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              6. Data Retention
            </h2>
            <p>
              We do not store personal data on our servers. Analytics data is
              retained by Google according to their data retention policies.
              Local storage data remains on your device until you clear your
              browser data.
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              7. Children's Privacy
            </h2>
            <p>
              SpacaSnake.io is a family-friendly game. We do not knowingly
              collect personal information from children under the age of 13 (or
              the applicable age in your jurisdiction). If you are a parent or
              guardian and believe your child has provided us with personal
              information, please contact us so we can take appropriate action.
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              8. Your Rights
            </h2>
            <p className="mb-2">
              Depending on your location, you may have the right to:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Access the personal data we hold about you.</li>
              <li>Request deletion of your personal data.</li>
              <li>Opt out of personalized advertising.</li>
              <li>Withdraw consent for cookie usage.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              9. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will
              be posted on this page with an updated "Last updated" date. Your
              continued use of the Service after changes constitutes acceptance
              of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              10. Contact Us
            </h2>
            <p>
              If you have questions about this Privacy Policy, please contact us
              at{' '}
              <a
                href="mailto:privacy@spacasnake.io"
                className="text-primary hover:underline"
              >
                privacy@spacasnake.io
              </a>
              .
            </p>
          </section>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10">
          <Link
            to="/"
            className="text-primary hover:underline text-sm font-display"
          >
            &larr; Back to game
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
